import { runMount, runPageIn } from "@/modules/_/runner";

runPageIn();
runMount();

import { Marquee } from "@/modules/marquee";
import { DotsAnimation } from "@/modules/dots-animation";
import { TextAnimations } from "@/modules/text-animations";
import { CoinsAnimation } from "@/modules/coins-animation";

const isMobile = window.matchMedia("(max-width: 991px)").matches;
const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

if (!isMobile) {
  const textAnimations = new TextAnimations("#heroSection [data-split-inview]");
  textAnimations.init();
}

const marquee = new Marquee();
marquee.init();
const dotsAnimation = new DotsAnimation("#heroSection [data-dots]");
dotsAnimation.init();

if (!isMobile && !reduceMotion) {
  const heroCoins = new CoinsAnimation("riveHero", "heroSection", 0.8, "top 0%", "bottom top");
  heroCoins.init();
}

const getScriptOrigin = () => {
  const currentScript = document.currentScript as HTMLScriptElement | null;
  const scriptUrl = currentScript?.src;

  return scriptUrl ? new URL(scriptUrl).origin : window.location.origin;
};

const homeNoncriticalUrl = new URL("/src/pages/home-noncritical.js", getScriptOrigin()).href;

let homeNoncriticalPromise: Promise<void> | null = null;

const loadHomeNoncritical = () => {
  if (homeNoncriticalPromise) return homeNoncriticalPromise;

  homeNoncriticalPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = homeNoncriticalUrl;
    script.async = true;
    script.dataset.homeNoncritical = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${homeNoncriticalUrl}`));

    document.head.appendChild(script);
  });

  return homeNoncriticalPromise;
};

const loadHomeNoncriticalWithWarning = () => {
  loadHomeNoncritical().catch((error) => {
    console.warn("Failed to load noncritical home animations", error);
  });
};

const setupHomeNoncriticalScrollFallback = () => {
  const events = ["scroll", "wheel", "touchmove", "keydown"];
  const cleanup = () => {
    events.forEach((event) => {
      window.removeEventListener(event, loadOnce);
    });
  };
  const loadOnce = () => {
    cleanup();
    loadHomeNoncriticalWithWarning();
  };

  events.forEach((event) => {
    window.addEventListener(event, loadOnce, { once: true, passive: true });
  });
};

const setupHomeNoncriticalLoader = () => {
  if (isMobile) {
    setupHomeNoncriticalScrollFallback();
    return;
  }

  const targets = Array.from(
    document.querySelectorAll("#main > section:not(#heroSection), #footerSection")
  );

  if (!targets.length) {
    setupHomeNoncriticalScrollFallback();
    return;
  }

  if (!("IntersectionObserver" in window)) {
    loadHomeNoncriticalWithWarning();
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;

    loadHomeNoncritical()
      .catch((error) => {
        console.warn("Failed to load noncritical home animations", error);
      })
      .finally(() => {
        observer.disconnect();
      });
  }, { rootMargin: "200px" });

  targets.forEach((target) => {
    observer.observe(target);
  });
};

setupHomeNoncriticalLoader();

history.scrollRestoration = "manual";
window.scrollTo(0, 0);
